/**
 * Custom hook for managing the theme service.
 * @returns An object with the `activateTheme` function.
 */

import themesJSON from '../assets/data/themes.json';

export const useThemeService = () => {
  const themeStore = useThemeStore();

  /**
   * Activates the specified theme.
   * @param themeCode - The code of the theme to activate. If not provided or incorrect, the default theme will be set as active.
   * @param ignoreLocalStorage - If true, the theme will not be stored in local storage.
   */
  const activateTheme = (themeCode?: string, ignoreLocalStorage = false) => {
    let isThemeLoaded = false;
    const themeKey = useLocalStorage().theme;

    // Load the specified theme if a theme code is provided, this will be only loaded if it's a valid theme
    if (
      themeCode &&
      Object.prototype.hasOwnProperty.call(themesJSON, themeStore.activeTheme)
    ) {
      isThemeLoaded = themeStore.load(themeCode);
    }
    // If no theme is loaded or the theme was incorrect, reset to the default theme
    if (!isThemeLoaded) {
      themeStore.reset();
    }

    // If client browser(not SSR) and ignoreLocalStorage is false, store the active theme in local storage
    if (!process.server && !ignoreLocalStorage) {
      localStorage.setItem(themeKey, themeStore.activeTheme);
    }

    const activeThemeData =
      themeStore.activeTheme &&
      (
        themesJSON as Record<
          string,
          {
            meta: {
              code: string;
              id: string;
              name: string;
              description: string;
            };
            properties: Record<string, string>;
          }
        >
      )[themeStore.activeTheme];
    // If a theme is successfully loaded, apply the theme properties to the document
    if (activeThemeData) {
      const themeVariables: Record<string, string> = {};
      Object.keys(activeThemeData.properties).forEach(function (property) {
        // Set the property value in the theme variables object
        themeVariables[property] = activeThemeData.properties[property];

        // If the property value is a valid hexadecimal color code, convert it to RGB and set the - value
        if (_isValidHex(activeThemeData.properties[property])) {
          themeVariables[property] = `rgb(${_hexToRGB(
            activeThemeData.properties[property]
          )})`;
          themeVariables[property + '-rgb'] = _hexToRGB(
            activeThemeData.properties[property]
          );
        }
      });
      return (
        ':root{' +
        Object.keys(themeVariables)
          .map((key) => `${key}:${themeVariables[key]};`)
          .join('') +
        '}'
      );
    }
  };

  /* Reset theme to default theme
		-	called when logging out
	*/
  const resetThemeToDefault = () => {
    themeStore.reset();
    if (!process.server) {
      localStorage.setItem(useLocalStorage().theme, themeStore.activeTheme);
    }
  };

  /**
   * Checks if the given string is a valid hexadecimal color code.
   * @param hex - The string to check.
   * @returns True if the string is a valid hexadecimal color code, false otherwise.
   */
  const _isValidHex = (hex: string) => {
    return hex.match(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/i);
  };

  /**
   * Converts a hexadecimal color code to its RGB representation.
   * @param hex - The hexadecimal color code to convert.
   * @param alphaValue - The alpha value to apply to the RGB color. If not provided, the alpha value from the hexadecimal color code will be used.
   * @returns The RGB representation of the color.
   */
  const _hexToRGB = (hex: string, alphaValue?: number) => {
    // Remove the '#' symbol from the beginning of the hex code
    hex = hex.replace(/^#/, '');

    let alphaFromHex = 1;

    // Check if the hex code has an alpha value (8 characters)
    if (hex.length === 8) {
      // Extract the alpha value from the hex code and convert it to a decimal value between 0 and 1
      alphaFromHex = Number.parseInt(hex.slice(6, 8), 16) / 255;

      // Remove the alpha value from the hex code
      hex = hex.slice(0, 6);
    }

    // Check if the hex code has a short form with an alpha value (4 characters)
    if (hex.length === 4) {
      // Extract the alpha value from the hex code and convert it to a decimal value between 0 and 1
      alphaFromHex = Number.parseInt(hex.slice(3, 4).repeat(2), 16) / 255;

      // Remove the alpha value from the hex code
      hex = hex.slice(0, 3);
    }

    // Check if the hex code has a short form without an alpha value (3 characters)
    if (hex.length === 3) {
      // Expand the short form to the full form by repeating each character
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    // Convert the hex code to a decimal number
    const number = Number.parseInt(hex, 16);

    // Extract the red, green, and blue values from the decimal number
    const red = number >> 16;
    const green = (number >> 8) & 255;
    const blue = number & 255;

    // Determine the alpha value to use for the RGB color
    const alpha = alphaValue ?? alphaFromHex;

    // Return the RGB representation of the color
    return alpha != 1
      ? `${red},${green},${blue},${alpha}`
      : `${red},${green},${blue}`;
  };

  return {
    activateTheme,
    resetThemeToDefault
  };
};
